import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarType } from '@xeetr/types/enums'
import { CalendarEvent, CalendarResource } from '@xeetr/types/models'
import { startOfDay } from 'date-fns'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useContext, useEffect, useMemo } from 'react'

import {
  calendarDateWithTimezoneAtom,
  calendarTypeAtom,
  setCalendarDateAtom,
} from '../atoms/calendar'
import { ActiveTenantContext } from '../context/common/Context'
import { invalidateTenantQueries } from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import CalendarService from '../services/Calendar.service'
import { SettingsService } from '../services/Settings.service'
import { getStartAndEndDatesForCalendar } from '../utils/calendar'

import { useErrorHandler } from './common/useErrorHandler'
import { useAuth } from './useAuth'

import { showErrorToastUnlessPermissionDenied } from '@/utils/toastMessage'

interface UseCalendarReturn {
  events: CalendarEvent[]
  eventsLoading: boolean
  eventsError: boolean

  resources: CalendarResource[]
  resourcesLoading: boolean
  resourcesError: boolean
  refetchResources: () => void

  date: Date
  calendarType: CalendarType
  setDate: (date: Date) => void
  setCalendarType: (type: CalendarType) => void
}

export const useCalendar = (): UseCalendarReturn => {
  const { getAccessTokenSilently } = useAuth()
  const { tenantId, tenant } = useContext(ActiveTenantContext)
  const queryClient = useQueryClient()

  const date = useAtomValue(calendarDateWithTimezoneAtom)
  const [calendarType, setCalendarTypeInternal] = useAtom(calendarTypeAtom)
  const setDate = useSetAtom(setCalendarDateAtom)

  const getCalendarService = useMemo(() => {
    return async () => {
      if (!tenant) throw new Error('No tenant')
      const token = await getAccessTokenSilently()
      return new CalendarService(token, tenant.id, tenant.type)
    }
  }, [tenant?.id, tenant?.type, getAccessTokenSilently])

  const getSettingsService = useMemo(() => {
    return async () => {
      if (!tenant) throw new Error('No tenant')
      const token = await getAccessTokenSilently()
      return new SettingsService(token, tenant.id, tenant.type)
    }
  }, [tenant?.id, tenant?.type, getAccessTokenSilently])

  useEffect(() => {
    if (tenant?.calendarType && calendarType !== tenant.calendarType) {
      setCalendarTypeInternal(tenant.calendarType)
    }
  }, [tenant?.calendarType, calendarType])

  const calendarTypeMutation = useMutation({
    mutationFn: async (newType: CalendarType) => {
      const service = await getSettingsService()
      return service.patchSettings({ calendarType: newType })
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings(tenantId), data)

      queryClient.invalidateQueries({ queryKey: queryKeys.user() })
      invalidateTenantQueries(tenantId)
    },
    onError: (error) => {
      console.error('Failed to update calendar type:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Speichern der Kalenderansicht.',
      })
    },
  })

  const setCalendarType = useCallback(
    (newType: CalendarType) => {
      setCalendarTypeInternal(newType)
      calendarTypeMutation.mutate(newType)
    },
    [calendarTypeMutation, setCalendarTypeInternal]
  )

  const normalizedDate = useMemo(() => startOfDay(date), [date])

  const eventsQuery = useQuery({
    queryKey: queryKeys.calendar.events(tenant?.id || '', normalizedDate, calendarType),
    queryFn: async () => {
      if (!tenant?.id) return []
      const { fromDate, untilDate } = getStartAndEndDatesForCalendar(
        normalizedDate,
        calendarType,
        tenant.timezone
      )
      const service = await getCalendarService()
      return service.getCalendarEvents(fromDate, untilDate, calendarType)
    },
    enabled: !!tenant?.id,
    staleTime: 1 * 60 * 1000,
    refetchInterval: (tenant?.refreshDuration || 300) * 1000,
    refetchIntervalInBackground: false,
  })

  useErrorHandler(eventsQuery.isError, 'Fehler beim Laden der Kalendereinträge.')

  const resourcesQuery = useQuery({
    queryKey: queryKeys.calendar.resources(tenant?.id || ''),
    queryFn: async () => {
      if (!tenant?.id) return []
      const service = await getCalendarService()
      return service.getCalendarResources()
    },
    enabled: !!tenant?.id,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (tenant?.refreshDuration || 300) * 1000,
    refetchIntervalInBackground: false,
  })

  useErrorHandler(resourcesQuery.isError, 'Fehler beim Laden der Kalenderressourcen.')

  return {
    events: eventsQuery.data || [],
    eventsLoading: eventsQuery.isLoading,
    eventsError: eventsQuery.isError,

    resources: resourcesQuery.data || [],
    resourcesLoading: resourcesQuery.isLoading,
    resourcesError: resourcesQuery.isError,
    refetchResources: () => {
      resourcesQuery.refetch()
    },

    date: normalizedDate,
    calendarType,
    setDate,
    setCalendarType,
  }
}
